/**
 * Simulador de costo de infraestructura al tope de límites del plan.
 * Tasas en USD (editables en UI). Conversión a MXN con tipo de cambio.
 *
 * Referencia del producto (OpenAI Realtime en backend):
 *   REALTIME_AUDIO_COST_PER_MINUTE_USD.openai_realtime = 0.15
 * Twilio México outbound es variable; default de ejemplo ~0.03 USD/min por pierna.
 */

export const DEFAULT_FX_USD_TO_MXN = 20;

/** Costos unitarios de ejemplo en USD — ajustables por el super admin. */
export const DEFAULT_UNIT_COSTS_USD = {
  /** Telefonía Twilio por minuto y por pierna (1 llamada = 1 pierna). */
  telephony_per_minute: 0.03,
  /** Audio OpenAI Realtime (estimado backend). */
  realtime_ai_per_minute: 0.15,
  /** Conversación WhatsApp (Meta) — orden de magnitud; ajusta con tu factura. */
  whatsapp_conversation: 0.05,
  /** Tokens de chat IA: USD por 1,000 tokens (mezcla input/output). */
  ai_tokens_per_1k: 0.01,
  /** Renta mensual de número de voz (Twilio). */
  voice_number_monthly: 1.15,
  /** Renta / fee aproximado de número WhatsApp por mes. */
  whatsapp_number_monthly: 0,
  /** Emails salientes (infra propia) por envío. */
  email_send: 0.001,
  /** SMS por mensaje. */
  sms_send: 0.04,
};

/**
 * Minuto de llamada con IA (1 pierna Twilio + Realtime).
 * @returns {{ usd: number, mxn: number, parts: object }}
 */
export function costPerAiVoiceMinute(units = DEFAULT_UNIT_COSTS_USD, fx = DEFAULT_FX_USD_TO_MXN) {
  const telephony = Number(units.telephony_per_minute) || 0;
  const realtime = Number(units.realtime_ai_per_minute) || 0;
  const usd = telephony + realtime;
  return {
    usd,
    mxn: usd * fx,
    parts: {
      telephony_usd: telephony,
      realtime_usd: realtime,
      telephony_mxn: telephony * fx,
      realtime_mxn: realtime * fx,
    },
  };
}

/**
 * Minuto de llamada enlazada lead–vendedor (solo telefonía).
 * mode: 'ivr' = 1 pierna, 'bridge' = 2 piernas concurrentes.
 */
export function costPerLinkedMinute(mode = 'ivr', units = DEFAULT_UNIT_COSTS_USD, fx = DEFAULT_FX_USD_TO_MXN) {
  const legs = mode === 'bridge' ? 2 : 1;
  const perLeg = Number(units.telephony_per_minute) || 0;
  const usd = perLeg * legs;
  return { usd, mxn: usd * fx, legs, per_leg_usd: perLeg };
}

/**
 * Costo máximo mensual si el tenant agota todos los límites del plan
 * y usa infraestructura de plataforma (cuando el entitlement está on).
 */
export function simulatePlanMaxCost({
  priceMonthly = 0,
  currency = 'MXN',
  includedLimits = {},
  includedEntitlements = {},
  unitCostsUsd = DEFAULT_UNIT_COSTS_USD,
  fxUsdToMxn = DEFAULT_FX_USD_TO_MXN,
  /** % de los minutos de voz que estimas serán enlace (no IA). 0–100 */
  linkedShareOfVoiceMinutes = 20,
  linkedMode = 'ivr',
}) {
  const limits = includedLimits || {};
  const ents = includedEntitlements || {};
  const usesPlatformInfra = !!ents.platform_infrastructure_enabled;
  const voiceEnabled = !!ents.voice_enabled || !!ents.voice_ai_enabled || !!ents.voice_realtime_enabled;
  const fx = Number(fxUsdToMxn) || DEFAULT_FX_USD_TO_MXN;
  const u = { ...DEFAULT_UNIT_COSTS_USD, ...unitCostsUsd };

  const voiceMinutes = Math.max(0, Number(limits.max_voice_minutes_monthly) || 0);
  const linkedPct = Math.min(100, Math.max(0, Number(linkedShareOfVoiceMinutes) || 0)) / 100;
  const linkedMinutes = voiceEnabled ? voiceMinutes * linkedPct : 0;
  const aiMinutes = voiceEnabled ? voiceMinutes * (1 - linkedPct) : 0;

  const aiPerMin = costPerAiVoiceMinute(u, fx);
  const linkPerMin = costPerLinkedMinute(linkedMode, u, fx);

  const lines = [];

  if (voiceEnabled && aiMinutes > 0) {
    const usd = aiMinutes * aiPerMin.usd;
    lines.push({
      key: 'voice_ai',
      label: 'Minutos de voz con IA (tope del plan)',
      quantity: round2(aiMinutes),
      unit: 'min',
      unit_cost_usd: aiPerMin.usd,
      total_usd: usd,
      total_mxn: usd * fx,
      detail: `Twilio ${aiPerMin.parts.telephony_usd.toFixed(3)} + Realtime ${aiPerMin.parts.realtime_usd.toFixed(3)} USD/min`,
      only_if_platform_infra: true,
    });
  }

  if (voiceEnabled && linkedMinutes > 0) {
    const usd = linkedMinutes * linkPerMin.usd;
    lines.push({
      key: 'voice_linked',
      label: `Minutos enlazados lead–vendedor (${linkedMode === 'bridge' ? 'bridge 2 piernas' : 'IVR 1 pierna'})`,
      quantity: round2(linkedMinutes),
      unit: 'min',
      unit_cost_usd: linkPerMin.usd,
      total_usd: usd,
      total_mxn: usd * fx,
      detail: `Solo telefonía · ${linkPerMin.legs} pierna(s) × ${linkPerMin.per_leg_usd.toFixed(3)} USD/min`,
      only_if_platform_infra: true,
    });
  }

  const waConv = Math.max(0, Number(limits.max_whatsapp_conversations_monthly) || 0);
  if (waConv > 0) {
    const usd = waConv * (Number(u.whatsapp_conversation) || 0);
    lines.push({
      key: 'whatsapp',
      label: 'Conversaciones WhatsApp (tope del plan)',
      quantity: waConv,
      unit: 'conv',
      unit_cost_usd: Number(u.whatsapp_conversation) || 0,
      total_usd: usd,
      total_mxn: usd * fx,
      detail: 'Meta / proveedor WhatsApp (aprox.)',
      only_if_platform_infra: true,
    });
  }

  const aiTokens = Math.max(0, Number(limits.max_ai_tokens_monthly) || 0);
  if (aiTokens > 0) {
    const usd = (aiTokens / 1000) * (Number(u.ai_tokens_per_1k) || 0);
    lines.push({
      key: 'ai_tokens',
      label: 'Tokens de IA chat (tope del plan)',
      quantity: aiTokens,
      unit: 'tokens',
      unit_cost_usd: Number(u.ai_tokens_per_1k) || 0,
      total_usd: usd,
      total_mxn: usd * fx,
      detail: 'USD por cada 1,000 tokens',
      only_if_platform_infra: false,
    });
  }

  const voiceNums = Math.max(0, Number(limits.max_voice_instances) || 0);
  if (voiceNums > 0) {
    const usd = voiceNums * (Number(u.voice_number_monthly) || 0);
    lines.push({
      key: 'voice_numbers',
      label: 'Renta de números de voz',
      quantity: voiceNums,
      unit: 'números',
      unit_cost_usd: Number(u.voice_number_monthly) || 0,
      total_usd: usd,
      total_mxn: usd * fx,
      detail: 'Renta mensual Twilio',
      only_if_platform_infra: true,
    });
  }

  const waNums = Math.max(0, Number(limits.max_whatsapp_instances) || 0);
  if (waNums > 0 && (Number(u.whatsapp_number_monthly) || 0) > 0) {
    const usd = waNums * (Number(u.whatsapp_number_monthly) || 0);
    lines.push({
      key: 'wa_numbers',
      label: 'Renta / fee números WhatsApp',
      quantity: waNums,
      unit: 'números',
      unit_cost_usd: Number(u.whatsapp_number_monthly) || 0,
      total_usd: usd,
      total_mxn: usd * fx,
      detail: 'Si aplicas fee por línea en infra propia',
      only_if_platform_infra: true,
    });
  }

  const emails = Math.max(0, Number(limits.max_email_sends_monthly) || 0);
  if (emails > 0) {
    const usd = emails * (Number(u.email_send) || 0);
    lines.push({
      key: 'email',
      label: 'Emails salientes (tope del plan)',
      quantity: emails,
      unit: 'emails',
      unit_cost_usd: Number(u.email_send) || 0,
      total_usd: usd,
      total_mxn: usd * fx,
      detail: 'Proveedor de email',
      only_if_platform_infra: true,
    });
  }

  const sms = Math.max(0, Number(limits.max_sms_sends_monthly) || 0);
  if (sms > 0) {
    const usd = sms * (Number(u.sms_send) || 0);
    lines.push({
      key: 'sms',
      label: 'SMS (tope del plan)',
      quantity: sms,
      unit: 'sms',
      unit_cost_usd: Number(u.sms_send) || 0,
      total_usd: usd,
      total_mxn: usd * fx,
      detail: 'Twilio / proveedor SMS',
      only_if_platform_infra: true,
    });
  }

  // Si no hay infra de plataforma, los costos de canal (voz/WA/email) corren por cuenta del tenant.
  const activeLines = lines.filter((l) => !l.only_if_platform_infra || usesPlatformInfra);
  const totalUsd = activeLines.reduce((s, l) => s + l.total_usd, 0);
  const totalMxn = totalUsd * fx;

  const sale = Number(priceMonthly) || 0;
  const saleIsMxn = (currency || 'MXN').toUpperCase() === 'MXN';
  const saleMxn = saleIsMxn ? sale : sale * fx;
  const marginMxn = saleMxn - totalMxn;
  const marginPct = saleMxn > 0 ? (marginMxn / saleMxn) * 100 : null;

  return {
    uses_platform_infrastructure: usesPlatformInfra,
    fx_usd_to_mxn: fx,
    unit_costs_usd: u,
    reference: {
      ai_voice_per_minute: costPerAiVoiceMinute(u, fx),
      linked_ivr_per_minute: costPerLinkedMinute('ivr', u, fx),
      linked_bridge_per_minute: costPerLinkedMinute('bridge', u, fx),
    },
    lines: activeLines,
    skipped_platform_lines: !usesPlatformInfra
      ? lines.filter((l) => l.only_if_platform_infra).length
      : 0,
    totals: {
      cost_usd: round2(totalUsd),
      cost_mxn: round2(totalMxn),
      sale_monthly: sale,
      sale_currency: currency || 'MXN',
      sale_mxn: round2(saleMxn),
      margin_mxn: round2(marginMxn),
      margin_pct: marginPct == null ? null : round2(marginPct),
    },
    assumptions: {
      voice_minutes_cap: voiceMinutes,
      ai_minutes: round2(aiMinutes),
      linked_minutes: round2(linkedMinutes),
      linked_share_pct: linkedPct * 100,
      linked_mode: linkedMode,
    },
  };
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}
